import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  REFUND_REASON_LABELS,
  type AdminOrderDetail,
  type AdminPaymentQueryResponse,
  type CurrencyCode,
  type RefundReason,
} from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCancelOrderMutation,
  useConfirmPaymentMutation,
  useDismissPaymentIssueMutation,
  useRefundOrderMutation,
  useRefundPaymentMutation,
  useUpdateOrderBuyerMutation,
} from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { PAYMENT_QUERY_STATUS_LABELS, dateTime, money } from "@/lib/format";

interface OrderDialogProps {
  detail: AdminOrderDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const REFUND_REASONS = Object.keys(REFUND_REASON_LABELS) as RefundReason[];

/** Organizatör panelindeki iade akışının aynısı; kayıtlarda aktör "TicketNjoy" görünür. */
export function OrderRefundDialog({ detail, open, onOpenChange }: OrderDialogProps) {
  const { order } = detail;
  const currency = order.currency as CurrencyCode;
  const refund = useRefundOrderMutation(order.orderNumber);
  const activeTickets = useMemo(
    () => order.tickets.filter((t) => t.status === "Valid"),
    [order.tickets],
  );
  const itemMap = useMemo(() => new Map(order.items.map((i) => [i.id, i])), [order.items]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState<RefundReason>("CustomerRequest");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);

  // Her açılışta form sıfırlanır; varsayılan seçim bütün geçerli biletler.
  const [preparedFor, setPreparedFor] = useState(false);
  if (open !== preparedFor) {
    setPreparedFor(open);
    if (open) {
      setSelected(new Set(activeTickets.map((t) => t.id)));
      setReason("CustomerRequest");
      setNote("");
      setSendEmail(true);
      setSendSms(true);
    }
  }

  const perTicket = (orderItemId: string) => {
    const item = itemMap.get(orderItemId);
    if (!item || item.isFree) return 0;
    const base = item.quantity > 0 ? item.subtotal / item.quantity : 0;
    return base + item.taxAmount + item.serviceFee;
  };
  const amount =
    Math.round(
      activeTickets.filter((t) => selected.has(t.id)).reduce((sum, t) => sum + perTicket(t.orderItemId), 0) *
        100,
    ) / 100;
  const allSelected = selected.size === activeTickets.length;
  const noteRequired = reason === "Other";
  const valid = selected.size > 0 && (!noteRequired || note.trim().length > 0);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSubmit() {
    try {
      await refund.mutateAsync({
        reason,
        reasonNote: note.trim() || null,
        ticketIds: allSelected ? null : [...selected],
        sendEmail,
        sendSms: sendSms && !!order.buyerPhone,
      });
      toast.success(allSelected ? "Sipariş iade edildi." : `${selected.size} bilet iade edildi.`);
      onOpenChange(false);
    } catch (err) {
      showApiError(err, "İade yapılamadı.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose={refund.isPending}>
      <DialogContent>
        <DialogHeader
          title="Sipariş iade edilsin mi?"
          description={`${order.orderNumber} · ${detail.organizerName} · ${order.eventTitle}`}
        />
        <div className="space-y-5 text-sm">
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">Biletler</span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() =>
                  setSelected(allSelected ? new Set() : new Set(activeTickets.map((t) => t.id)))
                }
              >
                {allSelected ? "Seçimi kaldır" : "Tümünü seç"}
              </button>
            </div>
            <ul className="max-h-48 divide-y overflow-y-auto rounded-md border">
              {activeTickets.map((t) => (
                <li key={t.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/40">
                    <input
                      type="checkbox"
                      className="size-4"
                      checked={selected.has(t.id)}
                      onChange={() => toggle(t.id)}
                    />
                    <span className="flex-1">
                      <span className="block font-mono text-xs">{t.ticketCode}</span>
                      <span className="block text-xs text-muted-foreground">{t.ticketTypeName}</span>
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {money(perTicket(t.orderItemId), currency)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-2">
            <Label>Sebep</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as RefundReason)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFUND_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {REFUND_REASON_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              rows={2}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                noteRequired ? "Açıklama (zorunlu)" : "Açıklama (isteğe bağlı, organizatör görür)"
              }
            />
          </section>

          <section className="space-y-2">
            <span className="font-medium">Alıcıya bildirim</span>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="size-4"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              E-posta ({order.buyerEmail})
            </label>
            <label className={order.buyerPhone ? "flex items-center gap-2" : "flex items-center gap-2 opacity-60"}>
              <input
                type="checkbox"
                className="size-4"
                checked={sendSms && !!order.buyerPhone}
                disabled={!order.buyerPhone}
                onChange={(e) => setSendSms(e.target.checked)}
              />
              SMS {order.buyerPhone ? `(${order.buyerPhone})` : "(telefon yok)"}
            </label>
          </section>

          <div className="flex items-baseline justify-between rounded-md bg-muted/40 p-3">
            <span className="text-muted-foreground">Alıcının kartına iade</span>
            <span className="text-base font-semibold tabular-nums">{money(amount, currency)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Para ödeme sağlayıcısından iade edilir; biletler geçersiz olur, stok satışa döner. İşlem
            organizatörün Aktivite kaydında "TicketNjoy" olarak görünür.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={refund.isPending}>
            Vazgeç
          </Button>
          <Button variant="destructive" onClick={onSubmit} disabled={!valid || refund.isPending}>
            {refund.isPending ? "İade ediliyor..." : allSelected ? "Tamamını iade et" : "Seçilenleri iade et"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrderCancelDialog({ detail, open, onOpenChange }: OrderDialogProps) {
  const { order } = detail;
  const cancel = useCancelOrderMutation(order.orderNumber);
  const [reason, setReason] = useState("");
  const paymentStarted = !!order.payment?.provider;

  async function onSubmit() {
    try {
      await cancel.mutateAsync({ reason: reason.trim() });
      toast.success("Sipariş iptal edildi.");
      setReason("");
      onOpenChange(false);
    } catch (err) {
      showApiError(err, "Sipariş iptal edilemedi.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose={cancel.isPending}>
      <DialogContent>
        <DialogHeader
          title="Bekleyen sipariş iptal edilsin mi?"
          description={`Ayrılan biletler satışa döner. Ödeme süresi ${dateTime(detail.expiresAt)} tarihinde doluyor; dokunulmazsa sipariş o zaman kendiliğinden kapanır.`}
        />
        {paymentStarted && (
          <div className="mb-4 flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <span>
              Bu siparişte ödeme başlatılmış. Alıcı banka sayfasındaysa ödeme yine de tamamlanabilir;
              iptalden önce alıcıyla görüş.
            </span>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="cancel-reason">Sebep</Label>
          <Textarea
            id="cancel-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Organizatör Aktivite kaydında bu metni görür."
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={cancel.isPending}>
            Vazgeç
          </Button>
          <Button
            variant="destructive"
            onClick={onSubmit}
            disabled={cancel.isPending || reason.trim().length < 3}
          >
            {cancel.isPending ? "İptal ediliyor..." : "İptal et"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PaymentDialogProps extends OrderDialogProps {
  /** Son "Bankaya sor" sonucu; sunucu işlemden önce yeniden sorar. */
  query: AdminPaymentQueryResponse | null;
}

function isPaymentVerified(query: AdminPaymentQueryResponse | null): boolean {
  return query?.status === "Paid" && query.amountMatches;
}

function Warning({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}

/** Banka doğrulamadıysa sebep zorunlu; kayıtta "banka doğrulamadan onay" görünür. */
export function ConfirmPaymentDialog({ detail, query, open, onOpenChange }: PaymentDialogProps) {
  const { order, payment } = detail;
  const currency = order.currency as CurrencyCode;
  const confirm = useConfirmPaymentMutation(order.orderNumber);
  const verified = isPaymentVerified(query);
  const [reason, setReason] = useState("");

  const [preparedFor, setPreparedFor] = useState(false);
  if (open !== preparedFor) {
    setPreparedFor(open);
    if (open) setReason("");
  }

  async function onSubmit() {
    try {
      await confirm.mutateAsync({ override: !verified, reason: reason.trim() || null });
      toast.success("Sipariş onaylandı, biletler kesildi. Alıcıya onay e-postası gönderiliyor.");
      onOpenChange(false);
    } catch (err) {
      showApiError(err, "Sipariş onaylanamadı.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose={confirm.isPending}>
      <DialogContent>
        <DialogHeader
          title="Ödeme onaylansın, biletler kesilsin mi?"
          description={`${order.orderNumber} · ${detail.organizerName} · ${money(payment.expectedAmount, currency)}`}
        />
        <div className="space-y-4 text-sm">
          {verified ? (
            <p>
              Banka {money(query!.amount ?? payment.expectedAmount, currency)} tahsilatı doğruladı.
            </p>
          ) : (
            <Warning>
              <p className="font-medium">
                {query
                  ? `Banka ödemeyi doğrulamadı: ${PAYMENT_QUERY_STATUS_LABELS[query.status]}${
                      query.status === "Paid" && !query.amountMatches
                        ? ` (bankadaki tutar ${query.amount === null ? "yok" : money(query.amount, currency)}, beklenen ${money(query.expectedAmount, currency)})`
                        : ""
                    }.`
                  : "Bankaya henüz sorulmadı."}
              </p>
              <p>
                Yine de onaylarsan biletler kesilir; para gerçekten alınmadıysa organizatöre ödenmemiş
                bir satış yazılır. Kayıtta "banka doğrulamadan onay" görünür.
              </p>
            </Warning>
          )}
          <p className="text-muted-foreground">
            Biletler kesilir, alıcıya normal onay e-postası ve SMS'i gider. Bilet stoku satışa açık
            adetten yeniden alınır; yetmezse onay yapılamaz, parayı iade et. Onaydan hemen önce bankaya
            yeniden sorulur.
          </p>
          {!verified && (
            <div className="space-y-2">
              <Label htmlFor="confirm-reason">Sebep (zorunlu)</Label>
              <Textarea
                id="confirm-reason"
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Örn. banka ekstresinde tahsilat görüldü, dekont no …"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={confirm.isPending}>
            Vazgeç
          </Button>
          <Button
            variant={verified ? "default" : "destructive"}
            onClick={onSubmit}
            disabled={confirm.isPending || (!verified && reason.trim().length < 3)}
          >
            {confirm.isPending
              ? "Onaylanıyor..."
              : verified
                ? "Onayla ve biletleri kes"
                : "Doğrulamadan onayla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Biletler kesilmeden para iade edilir; sipariş durumu değişmez. */
export function RefundPaymentDialog({ detail, query, open, onOpenChange }: PaymentDialogProps) {
  const { order, payment } = detail;
  const currency = order.currency as CurrencyCode;
  const refund = useRefundPaymentMutation(order.orderNumber);
  const [reason, setReason] = useState("");
  const amount = query?.status === "Paid" && query.amount !== null ? query.amount : payment.expectedAmount;

  const [preparedFor, setPreparedFor] = useState(false);
  if (open !== preparedFor) {
    setPreparedFor(open);
    if (open) setReason("");
  }

  async function onSubmit() {
    try {
      await refund.mutateAsync({ reason: reason.trim() });
      toast.success("Para iade edildi, ödeme sorunu kapandı.");
      onOpenChange(false);
    } catch (err) {
      showApiError(err, "İade yapılamadı.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose={refund.isPending}>
      <DialogContent>
        <DialogHeader
          title="Para biletsiz iade edilsin mi?"
          description={`${order.orderNumber} · ${detail.organizerName}`}
        />
        <div className="space-y-4 text-sm">
          <div className="flex items-baseline justify-between rounded-md bg-muted/40 p-3">
            <span className="text-muted-foreground">Alıcının kartına iade</span>
            <span className="text-base font-semibold tabular-nums">{money(amount, currency)}</span>
          </div>
          <p className="text-muted-foreground">
            Para ödeme sağlayıcısında iade edilir, bilet kesilmez. Sipariş durumu değişmez; sorun "iade
            edildi" olarak kapanır. Alıcıya otomatik e-posta gitmez. İşlemden önce bankaya yeniden
            sorulur; bankada tahsilat yoksa iade yapılmaz.
          </p>
          <div className="space-y-2">
            <Label htmlFor="payment-refund-reason">Sebep (zorunlu)</Label>
            <Textarea
              id="payment-refund-reason"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Örn. stok kalmadı, alıcı vazgeçti"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={refund.isPending}>
            Vazgeç
          </Button>
          <Button
            variant="destructive"
            onClick={onSubmit}
            disabled={refund.isPending || reason.trim().length < 3}
          >
            {refund.isPending ? "İade ediliyor..." : "Parayı iade et"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DismissPaymentIssueDialog({ detail, query, open, onOpenChange }: PaymentDialogProps) {
  const { order } = detail;
  const dismiss = useDismissPaymentIssueMutation(order.orderNumber);
  const [note, setNote] = useState("");

  const [preparedFor, setPreparedFor] = useState(false);
  if (open !== preparedFor) {
    setPreparedFor(open);
    if (open) setNote("");
  }

  async function onSubmit() {
    try {
      await dismiss.mutateAsync({ note: note.trim() });
      toast.success("Sorun kapatıldı.");
      onOpenChange(false);
    } catch (err) {
      showApiError(err, "Sorun kapatılamadı.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose={dismiss.isPending}>
      <DialogContent>
        <DialogHeader
          title="Bankada ödeme yok, sorun kapatılsın mı?"
          description={`${order.orderNumber} · ${detail.organizerName}`}
        />
        <div className="space-y-4 text-sm">
          {query && query.status !== "NotPaid" && query.status !== "NotFound" && (
            <Warning>
              <p>
                Son sorgu: {PAYMENT_QUERY_STATUS_LABELS[query.status]}. Bankanın yanıtından emin değilsen
                kapatma.
              </p>
            </Warning>
          )}
          <p className="text-muted-foreground">
            Alıcı banka sayfasını kapatmış ya da ödeme yarıda kalmışsa siparişi Sorunlu listesinden
            çıkarır. Sipariş olduğu gibi kalır, kimseye bildirim gitmez. Kapatmadan önce bankaya yeniden
            sorulur; bankada ödeme görünürse kapatılmaz.
          </p>
          <div className="space-y-2">
            <Label htmlFor="dismiss-note">Not (zorunlu)</Label>
            <Textarea
              id="dismiss-note"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Örn. banka sorgusu işlem yok dedi"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={dismiss.isPending}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={dismiss.isPending || note.trim().length < 3}>
            {dismiss.isPending ? "Kapatılıyor..." : "Sorunu kapat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrderBuyerDialog({ detail, open, onOpenChange }: OrderDialogProps) {
  const { order } = detail;
  const update = useUpdateOrderBuyerMutation(order.orderNumber);
  const [form, setForm] = useState({
    firstName: order.buyerFirstName,
    lastName: order.buyerLastName,
    email: order.buyerEmail,
    phone: order.buyerPhone,
  });

  const [preparedFor, setPreparedFor] = useState(false);
  if (open !== preparedFor) {
    setPreparedFor(open);
    if (open) {
      setForm({
        firstName: order.buyerFirstName,
        lastName: order.buyerLastName,
        email: order.buyerEmail,
        phone: order.buyerPhone,
      });
    }
  }

  const trimmed = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    phone: form.phone.trim(),
  };
  const dirty =
    trimmed.firstName !== order.buyerFirstName ||
    trimmed.lastName !== order.buyerLastName ||
    trimmed.email !== order.buyerEmail ||
    trimmed.phone !== order.buyerPhone;
  const valid =
    trimmed.firstName.length > 0 &&
    trimmed.lastName.length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.email) &&
    trimmed.phone.length >= 7;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || !valid) return;
    try {
      await update.mutateAsync(trimmed);
      toast.success("Alıcı bilgisi güncellendi. Biletleri yeni adrese göndermek için Tekrar gönder'i kullan.");
      onOpenChange(false);
    } catch (err) {
      showApiError(err, "Alıcı bilgisi güncellenemedi.");
    }
  }

  const field = (key: keyof typeof form) => ({
    id: `buyer-${key}`,
    value: form[key],
    onChange: (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose={update.isPending}>
      <DialogContent>
        <DialogHeader
          title="Alıcı bilgisini düzelt"
          description="Biletler yeni adrese kendiliğinden gitmez; kaydettikten sonra Tekrar gönder ile gönder."
        />
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="buyer-firstName">Ad</Label>
              <Input maxLength={100} {...field("firstName")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="buyer-lastName">Soyad</Label>
              <Input maxLength={100} {...field("lastName")} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="buyer-email">E-posta</Label>
            <Input type="email" maxLength={320} {...field("email")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="buyer-phone">Telefon</Label>
            <Input type="tel" maxLength={30} {...field("phone")} />
          </div>
          {order.invoiceAvailable && (
            <p className="text-xs text-muted-foreground">
              Bu siparişin faturası kesilmiş; fatura eski bilgilerle kalır.
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Vazgeç
            </Button>
            <Button type="submit" disabled={!dirty || !valid || update.isPending}>
              {update.isPending ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
